import React from 'react';
import { Card, Title, Text, Grid, Badge, Stack, useMantineTheme, Group } from '@mantine/core';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { RequestTypeStatistics } from '../../../types/statistics-types';

interface RequestTypeChartProps {
  data: RequestTypeStatistics[];
}

export function RequestTypeChart({ data }: RequestTypeChartProps) {
  const theme = useMantineTheme();
  
  // Ordina i dati per numero di richieste
  const sortedData = [...data].sort((a, b) => b.count - a.count);
  
  // Formatta i dati per il grafico a torta
  const chartData = sortedData.map(item => ({
    name: getTypeLabel(item.type),
    value: item.count,
    percentage: item.percentage,
  }));
  
  // Colori per il grafico a torta
  const COLORS = [
    theme.colors.blue[5],
    theme.colors.green[5],
    theme.colors.yellow[5],
    theme.colors.orange[5],
  ];
  
  return (
    <Card withBorder p="md">
      <Title order={3} mb="md">Distribuzione per Tipo di Richiesta</Title>
      <Text size="sm" color="dimmed" mb="xl">
        Visualizzazione delle richieste suddivise per tipologia
      </Text>
      
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={true}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: theme.colors.gray[0],
                  borderColor: theme.colors.gray[4],
                  borderRadius: theme.radius.sm
                }}
                formatter={(value, name, props) => [`${value} richieste (${props.payload.percentage.toFixed(1)}%)`, name]}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Stack gap="md">
            <Title order={4}>Dettaglio Richieste per Tipo</Title>
            
            {sortedData.map(item => (
              <Card key={item.type} withBorder p="sm">
                <Group justify="apart">
                  <Stack gap={0}>
                    <Title order={5}>{getTypeLabel(item.type)}</Title>
                    <Text size="sm" color="dimmed">
                      {item.count} richieste totali
                    </Text>
                  </Stack>
                  <Badge size="lg" variant="filled" color={getTypeColor(item.type)} style={{ color: 'white' }}>
                    {item.percentage.toFixed(1)}%
                  </Badge>
                </Group>
              </Card>
            ))}
          </Stack>
        </Grid.Col>
      </Grid>
    </Card>
  );
}

// Helper function per ottenere l'etichetta del tipo di richiesta
// Supporta sia il formato numerico nuovo (RequestType enum: 1..4)
// sia gli identificativi legacy stringa.
function getTypeLabel(type: any): string {
  switch (Number(type)) {
    case 1: return 'Bozzetto';
    case 2: return 'Campionatura';
    case 3: return 'Produzione';
    case 4: return 'Gamma';
  }
  switch (type) {
    case 'sketch': return 'Bozzetto';
    case 'sample': return 'Campionatura';
    case 'production': return 'Produzione';
    default: return String(type);
  }
}

function getTypeColor(type: any): string {
  switch (Number(type)) {
    case 1: return 'blue';
    case 2: return 'yellow';
    case 3: return 'green';
    case 4: return 'violet';
  }
  switch (type) {
    case 'sketch': return 'blue';
    case 'sample': return 'yellow';
    case 'production': return 'green';
    default: return 'gray';
  }
}